import React, { useState, useRef, useEffect } from 'react';

interface ClickCardProps {
  children: React.ReactNode;
  // Pass a function to receive a `close` callback (lets the popover close
  // itself before opening a follow-on modal).
  content: React.ReactNode | ((close: () => void) => React.ReactNode);
}

export const ClickCard: React.FC<ClickCardProps> = ({ 
  children, 
  content,
}) => {
  const [show, setShow] = useState(false);
  const targetRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShow(!show);
  };

  // Position the popover
  useEffect(() => {
    if (show && targetRef.current && popoverRef.current) {
      const targetRect = targetRef.current.getBoundingClientRect();
      const popoverRect = popoverRef.current.getBoundingClientRect();
      
      // Position above the target by default
      let top = targetRect.top - popoverRect.height - 8;
      let left = targetRect.left + (targetRect.width / 2) - (popoverRect.width / 2);
      
      // Adjust if it goes off screen
      if (top < 0) {
        top = targetRect.bottom + 8;
      }
      if (left < 0) {
        left = 8;
      }
      if (left + popoverRect.width > window.innerWidth) {
        left = window.innerWidth - popoverRect.width - 8;
      }
      
      popoverRef.current.style.top = `${top}px`;
      popoverRef.current.style.left = `${left}px`;
    }
  }, [show]);

  // Close when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (show && targetRef.current && !targetRef.current.contains(event.target as Node) &&
          popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        setShow(false);
      }
    };

    if (show) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [show]);

  return (
    <>
      <div
        ref={targetRef}
        onClick={handleClick}
        className="inline-block cursor-pointer min-w-0 max-w-full"
      >
        {children}
      </div>
      {show && (
        <div
          ref={popoverRef}
          className="fixed z-50 rounded-lg shadow-2xl border border-gray-200 overflow-hidden bg-white"
          style={{ position: 'fixed' }}
        >
          {typeof content === 'function' ? content(() => setShow(false)) : content}
        </div>
      )}
    </>
  );
};